<script setup lang="ts">
// SATU import, sengaja — ringan.css meng-@import main.css + v3.css + gerak.css
// (urutan itu WAJIB) sebagai satu modul yang berdiri sendiri. Meng-import
// ketiganya langsung di sini akan membuat Vite mengangkatnya jadi chunk BERSAMA
// dengan layout `default`, dan CSS-nya lalu terkirim DUA KALI di setiap halaman
// situs. Alasan lengkapnya ada di kepala assets/css/ringan.css.
import '~/assets/css/ringan.css'

/**
 * Layout RINGAN — tampilan tema "Liquid CoreAsia" yang sama dengan layout
 * `default`, tapi tanpa satu pun komponen Nuxt UI.
 *
 * KENAPA ADA:
 * Layout `default` membungkus seluruh isinya dengan <UApp>, pembungkus root
 * Nuxt UI. Karena itu SETIAP halaman yang memakainya menanggung runtime Nuxt UI
 * (reka-ui, tailwind-variants, runtime ikon) — bukan karena isi halamannya,
 * melainkan karena layoutnya. Untuk /kompres-foto dan /kompres-pdf ongkos itu
 * tidak pernah dibayar kembali: mereka pintu masuk SEO yang dibuka orang dari
 * Google di HP Android murah lewat jaringan seluler, memakai satu alat, lalu
 * pergi. Halaman itu tidak butuh dialog, toast, atau tooltip.
 *
 * YANG SENGAJA TIDAK DIUBAH: penampilannya. Header, footer, tipografi, warna,
 * radius, dan bayangan di sini memakai token dan kelas yang SAMA PERSIS dengan
 * layout utama — bukan desain lama yang dihidupkan lagi. Pengunjung yang
 * mendarat di /kompres-foto lalu mengklik ke /berkaskilat (halaman berbayar,
 * layout `default`) tidak boleh merasa berpindah ke situs lain.
 *
 * <CaGlassBar> dan <CaLockup> DIPAKAI LANGSUNG, bukan disalin: keduanya sudah
 * murni CSS + SVG inline dan tidak menyentuh Nuxt UI sama sekali. Memakai
 * ulang berarti header di kedua layout dijamin identik, bukan sekadar mirip.
 *
 * Yang diganti versi ringannya hanya yang memang membungkus Nuxt UI:
 *   <CaButton>    (UButton)    → <RinganTombol>
 *   <CaSeparator> (USeparator) → <div role="separator">
 *   <CaDialog>    (UModal)     → panel menu sederhana di bawah
 *   <UIcon>                    → <RinganIkon> (SVG inline)
 */
const colorMode = useColorMode()
const menuTerbuka = ref(false)
const route = useRoute()
const { adaWa, adaEmail, tautanWa, tautanEmail, adaNama, namaBadanUsaha } = useIdentitas()

// Footer di layout ini juga ber-`.ca-tunda`, dan nav "Harga"/"Semua
// alat"/"Bantuan" menuju anchor lintas-halaman ke index.vue (layout
// `default`) — kena bug content-visibility yang sama. WAJIB dipanggil di
// SETIAP layout yang punya tautan anchor, bukan cuma `default`: lihat
// composables/useGulirAnchor.ts.
useGulirAnchor()

watch(() => route.fullPath, () => (menuTerbuka.value = false))

// Daftar tautan SENGAJA sama persis dengan layout `default`. Halaman kompres
// adalah tempat pengunjung pertama kali mendarat; kalau jalan menuju produk
// berbayar di sini berbeda dari halaman lain, jalan itu putus.
const tautan = [
  { ke: '/#alat', label: 'Semua alat' },
  { ke: '/berkaskilat', label: 'BerkasKilat' },
  { ke: '/#harga', label: 'Harga' },
  // Lihat catatan yang sama di layouts/default.vue.
  { ke: '/buku/melek-komputer', label: 'Ebook' },
  { ke: '/#bantuan', label: 'Bantuan' }
]

/**
 * Mode gelap memakai useColorMode() yang SAMA dengan layout utama, bukan
 * mekanisme kedua. Ini bukan kelalaian melainkan pilihan: composable itu milik
 * modul @nuxtjs/color-mode, bukan Nuxt UI, dan plugin-nya sudah berjalan global
 * untuk seluruh situs (ia yang memasang kelas `.dark` sebelum halaman tampil,
 * sehingga tidak ada kedipan tema). Jadi memakainya di sini tidak menarik satu
 * byte pun tambahan — sementara menulis sakelar tema sendiri justru akan
 * memecah kunci penyimpanan `ca-color-mode` dan membuat preferensi pengunjung
 * hilang saat berpindah dari halaman kompres ke halaman berbayar.
 */
const modeBerikut = computed(() => (colorMode.value === 'dark' ? 'light' : 'dark'))
function gantiTema() {
  colorMode.preference = modeBerikut.value
}

/* --- Perilaku menu HP -------------------------------------------------------
   Ditulis tangan karena <CaDialog> membungkus <UModal> (reka-ui). Yang ditiru
   hanya yang benar-benar dirasakan pengguna: ESC menutup, fokus masuk ke panel
   lalu kembali ke tombol pemicu, dan badan halaman berhenti bisa digulir di
   belakang panel. */
const panel = ref<HTMLElement | null>(null)
const pemicuTerakhir = ref<HTMLElement | null>(null)

function tutupSaatEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') menuTerbuka.value = false
}

watch(menuTerbuka, async (buka) => {
  // Hanya berjalan di peramban: layout ini juga dirender di server.
  if (!import.meta.client) return

  if (buka) {
    pemicuTerakhir.value = document.activeElement as HTMLElement | null
    document.addEventListener('keydown', tutupSaatEsc)
    document.body.style.overflow = 'hidden'
    await nextTick()
    panel.value?.focus()
  } else {
    document.removeEventListener('keydown', tutupSaatEsc)
    document.body.style.overflow = ''
    pemicuTerakhir.value?.focus()
    pemicuTerakhir.value = null
  }
})

// Berpindah halaman saat menu terbuka tidak boleh meninggalkan <body> terkunci.
onBeforeUnmount(() => {
  if (!import.meta.client) return
  document.removeEventListener('keydown', tutupSaatEsc)
  document.body.style.overflow = ''
})

const tahunIni = new Date().getFullYear()
</script>

<template>
  <div class="ca-root ca-v3 min-h-dvh flex flex-col">
    <NuxtRouteAnnouncer />

    <CaGlassBar posisi="atas">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <div class="flex items-center gap-3 h-16">
          <!-- min-h-11: lockup juga sebuah tautan, jadi ia ikut aturan tap target 44px. -->
          <NuxtLink
            to="/"
            class="ca-focus shrink-0 min-w-0 inline-flex items-center min-h-11"
            aria-label="CoreAsia Beres — beranda"
          >
            <CaLockup />
          </NuxtLink>

          <nav class="hidden lg:flex items-center gap-1 ms-4">
            <RinganTombol v-for="t in tautan" :key="t.ke" variant="ghost" size="sm" :to="t.ke">
              {{ t.label }}
            </RinganTombol>
          </nav>

          <div class="ms-auto flex items-center gap-2">
            <RinganTombol
              variant="ghost"
              size="md"
              class="w-11 px-0!"
              :icon="colorMode.value === 'dark' ? 'sun' : 'moon'"
              :aria-label="`Ganti ke tema ${modeBerikut === 'dark' ? 'gelap' : 'terang'}`"
              @click="gantiTema"
            />
            <!-- Wrapper polos: `hidden` di RinganTombol kalah oleh base `inline-flex`-nya
                 (tanpa tailwind-merge) sehingga tak pernah tersembunyi & bikin header
                 meluber di 360px. Span netral memastikan sembunyi < sm. -->
            <span class="hidden sm:inline-flex">
              <RinganTombol variant="secondary" size="md" to="/redeem">
                Tukar kode
              </RinganTombol>
            </span>
            <RinganTombol
              class="lg:hidden w-11 px-0!"
              variant="ghost"
              size="md"
              icon="menu"
              :aria-label="menuTerbuka ? 'Tutup menu' : 'Buka menu'"
              aria-haspopup="dialog"
              :aria-expanded="menuTerbuka"
              @click="menuTerbuka = !menuTerbuka"
            />
          </div>
        </div>
      </div>
    </CaGlassBar>

    <main class="flex-1">
      <slot />
    </main>

    <footer class="ca-tunda border-t border-line bg-surface-2 mt-20">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 py-12">
        <div class="grid gap-9 sm:grid-cols-2 lg:grid-cols-4">
          <div class="sm:col-span-2 lg:col-span-1">
            <CaLockup />
            <p class="ca-caption text-muted mt-3 leading-relaxed max-w-xs">
              Alat praktis untuk urusan berkas dan kontak. Bayar sekali, tanpa langganan.
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <p class="text-sm font-bold text-ink mb-1">Alat gratis</p>
            <RinganTombol variant="link" size="sm" to="/kompres-foto">Kompres foto</RinganTombol>
            <RinganTombol variant="link" size="sm" to="/kompres-pdf">Kompres PDF</RinganTombol>
          </div>

          <div class="flex flex-col gap-1">
            <p class="text-sm font-bold text-ink mb-1">Produk berbayar</p>
            <RinganTombol variant="link" size="sm" to="/berkaskilat">BerkasKilat</RinganTombol>
            <RinganTombol variant="link" size="sm" to="/rapikontak">RapiKontak</RinganTombol>
            <RinganTombol variant="link" size="sm" to="/buku/melek-komputer">Ebook Melek Komputer</RinganTombol>
            <RinganTombol variant="link" size="sm" to="/#harga">Harga</RinganTombol>
          </div>

          <div class="flex flex-col gap-1">
            <p class="text-sm font-bold text-ink mb-1">Perusahaan</p>
            <RinganTombol variant="link" size="sm" to="/syarat">Syarat layanan</RinganTombol>
            <RinganTombol variant="link" size="sm" to="/privasi">Kebijakan privasi</RinganTombol>
            <RinganTombol variant="link" size="sm" to="/refund">Ketentuan refund</RinganTombol>
            <!-- Kanal kontak hanya muncul bila datanya benar-benar ada.
                 Lihat composables/useIdentitas.ts — situs ini tidak pernah
                 memasang nomor atau email karangan. -->
            <RinganTombol
              v-if="adaWa"
              variant="link"
              size="sm"
              :href="tautanWa({ konteks: 'Footer' })"
            >WhatsApp</RinganTombol>
            <RinganTombol v-if="adaEmail" variant="link" size="sm" :href="tautanEmail()">
              Email
            </RinganTombol>
          </div>
        </div>

        <div class="my-9 border-t border-line" role="separator" />

        <p class="ca-caption text-muted max-w-3xl leading-relaxed">
          Batas ukuran berkas ditetapkan oleh masing-masing instansi dan bisa berubah
          sewaktu-waktu. Selalu periksa kembali pengumuman resmi instansi tujuan sebelum
          mengunggah.
        </p>
        <p class="ca-caption text-muted mt-3">
          <span v-if="adaNama">{{ namaBadanUsaha }} · </span>tools.coreasia.id · {{ tahunIni }}
        </p>
      </div>
    </footer>

    <!--
      Menu HP — padanan <CaDialog> (UModal / reka-ui) yang ditulis tangan.
      Bentuknya disalin dari `ui` milik CaDialog: bottom-sheet di HP (jempol ada
      di bawah layar, bukan di tengah), kotak tengah mulai dari `sm`.

      Yang ditiru dari reka-ui seperlunya, bukan seluruhnya: tutup dengan ESC,
      tutup saat menyentuh latar, fokus pindah ke panel saat dibuka lalu kembali
      ke tombol pemicu saat ditutup, dan gulir badan halaman dikunci. Jebakan
      fokus penuh sengaja tidak ditiru — isinya cuma lima tautan, dan menekan
      Tab sampai keluar panel akan mendarat di alamat peramban, bukan di kontrol
      tersembunyi yang membingungkan.
    -->
    <Teleport to="body">
      <div v-if="menuTerbuka" class="ca-v3 fixed inset-0 z-50">
        <div class="fixed inset-0 bg-black/60" @click="menuTerbuka = false" />

        <div
          ref="panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ringan-menu-judul"
          tabindex="-1"
          class="fixed inset-x-0 bottom-0 top-auto w-full max-w-full rounded-t-3xl bg-surface border border-line shadow-sheet sm:inset-x-auto sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-lg sm:rounded-[20px]"
        >
          <div class="px-5 pt-5 pb-3 flex items-start gap-3">
            <h2 id="ringan-menu-judul" class="ca-h3 text-ink">Menu</h2>
            <RinganTombol
              variant="ghost"
              size="md"
              class="ms-auto w-11 px-0! -me-1.5 -mt-1.5"
              icon="x"
              aria-label="Tutup menu"
              @click="menuTerbuka = false"
            />
          </div>

          <div class="px-5 pb-5">
            <nav class="flex flex-col gap-1.5">
              <RinganTombol
                v-for="t in tautan"
                :key="t.ke"
                variant="ghost"
                size="lg"
                block
                class="justify-start!"
                :to="t.ke"
              >{{ t.label }}</RinganTombol>

              <div class="my-2 border-t border-line" role="separator" />

              <RinganTombol variant="secondary" size="lg" block to="/redeem">
                Tukar kode aktivasi
              </RinganTombol>
            </nav>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
